/**
 * renderPlan — pure cell → draw-plan resolver for the viewer canvas.
 *
 * Separates "what to draw" (testable, canvas-free) from "how to draw it"
 * (CanvasRenderer.renderElement, which issues ctx calls). The editor emits three
 * JointJS cell shapes, any of which may ALSO carry a centered label:
 *   - standard.Path      → body.d (SVG path, local coords) + optional label
 *   - standard.Rectangle → body fill/stroke over size (no d) + optional label
 *                          (also used for point markers and transparent text labels)
 *   - standard.Image     → image.xlinkHref icon + optional label
 *
 * The previous viewer renderer treated these as mutually exclusive (a Rectangle
 * with no `d` drew nothing; a Path with a label drew only the label) and never
 * offset paths by the cell position. This resolver fixes both: every present
 * part is emitted, and all coordinates are local to `position` (the renderer
 * translates by position once before executing the plan).
 *
 * Labels are centered (cx/cy = size/2) to match the editor's
 * textAnchor:'middle' / textVerticalAnchor:'middle' label cells.
 */

import type { CanvasElement } from './types'

export interface PathPart {
  d: string
  fill?: string
  stroke?: string
  strokeWidth: number
}

export interface RectPart {
  width: number
  height: number
  fill?: string
  stroke?: string
  strokeWidth: number
}

export interface PolygonPart {
  /** Vertices in local (position-relative) coords, scaled to the cell size. */
  points: Array<{ x: number; y: number }>
  fill?: string
  stroke?: string
  strokeWidth: number
}

export interface ImagePart {
  href: string
  width: number
  height: number
}

export interface LabelPart {
  text: string
  fill: string
  fontSize: number
  fontFamily: string
  /** Center anchor in local (position-relative) coords. */
  cx: number
  cy: number
}

export interface RenderPlan {
  position: { x: number; y: number }
  opacity: number
  path?: PathPart
  rect?: RectPart
  polygon?: PolygonPart
  image?: ImagePart
  label?: LabelPart
}

const DEFAULT_LABEL_FILL = '#000'
const DEFAULT_FONT_SIZE = 12
const DEFAULT_FONT_FAMILY = 'Arial'

/** A paint value that produces no visible output. */
function isInvisiblePaint(value: string | undefined): boolean {
  return !value || value === 'none' || value === 'transparent'
}

/**
 * Parse a JointJS `refPoints` string ("x1,y1 x2,y2 …") into local coords
 * scaled to the cell size — the same fit JointJS applies to standard.Polygon.
 * Returns null when the string is malformed or has fewer than 3 vertices.
 */
function parseRefPoints(
  refPoints: string,
  size: { width: number; height: number }
): Array<{ x: number; y: number }> | null {
  const pts: Array<{ x: number; y: number }> = []
  for (const pair of refPoints.trim().split(/\s+/)) {
    const [x, y] = pair.split(',').map(Number)
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null
    pts.push({ x, y })
  }
  if (pts.length < 3) return null
  let minX = pts[0].x, maxX = pts[0].x, minY = pts[0].y, maxY = pts[0].y
  for (const p of pts) {
    if (p.x < minX) minX = p.x
    if (p.x > maxX) maxX = p.x
    if (p.y < minY) minY = p.y
    if (p.y > maxY) maxY = p.y
  }
  const w = maxX - minX || 1
  const h = maxY - minY || 1
  return pts.map((p) => ({
    x: ((p.x - minX) / w) * size.width,
    y: ((p.y - minY) / h) * size.height,
  }))
}

/**
 * Resolve a viewer CanvasElement into the set of primitives to draw.
 * Pure — no canvas, no side effects.
 */
export function planElementRender(element: CanvasElement): RenderPlan {
  const { attrs, position, size } = element
  const body = attrs.body
  const opacity = body?.opacity ?? attrs.image?.opacity ?? 1

  const plan: RenderPlan = {
    position: { x: position.x, y: position.y },
    opacity,
  }

  if (body) {
    const refPolygon = body.refPoints ? parseRefPoints(body.refPoints, size) : null
    if (body.d) {
      plan.path = {
        d: body.d,
        fill: body.fill,
        stroke: body.stroke,
        strokeWidth: body.strokeWidth ?? 1,
      }
    } else if (refPolygon && (!isInvisiblePaint(body.fill) || !isInvisiblePaint(body.stroke))) {
      // standard.Polygon (구역) — refPoints 정점을 그대로 그린다. 이전에는 이
      // 분기가 없어서 rect 폴백을 타 항상 바운딩 박스 사각형으로 보였다.
      plan.polygon = {
        points: refPolygon,
        fill: body.fill,
        stroke: body.stroke,
        strokeWidth: body.strokeWidth ?? 1,
      }
    } else if (!isInvisiblePaint(body.fill) || !isInvisiblePaint(body.stroke)) {
      // Rectangle body has no path data — draw the bounding box from size,
      // but only when it has at least one visible paint (skip transparent
      // label-carrier rectangles, which exist purely to host a label).
      plan.rect = {
        width: size.width,
        height: size.height,
        fill: body.fill,
        stroke: body.stroke,
        strokeWidth: body.strokeWidth ?? 1,
      }
    }
  }

  const href = attrs.image?.xlinkHref
  if (href) {
    // JointJS standard.Image 기본 attrs 는 width/height 를 "calc(w)"/"calc(h)"
    // 문자열(SVG calc 표현식)로 채운다. canvas drawImage 에 문자열이 들어가면
    // NaN 으로 강제돼 아무것도 그리지 않으므로(CCTV/비상벨 아이콘 투명 버그),
    // 숫자일 때만 쓰고 아니면 셀 size 로 대체한다.
    const w = attrs.image?.width
    const h = attrs.image?.height
    plan.image = {
      href,
      width: typeof w === 'number' ? w : size.width,
      height: typeof h === 'number' ? h : size.height,
    }
  }

  const labelText = attrs.label?.text
  if (labelText) {
    plan.label = {
      text: labelText,
      fill: attrs.label?.fill || DEFAULT_LABEL_FILL,
      fontSize: attrs.label?.fontSize || DEFAULT_FONT_SIZE,
      fontFamily: attrs.label?.fontFamily || DEFAULT_FONT_FAMILY,
      cx: size.width / 2,
      cy: size.height / 2,
    }
  }

  return plan
}
